import {
  AliyahType,
  HaftarahType,
  ParashahType,
  PracticeRecordingFocusPointType,
  PracticeRecordingType,
  SentenceGroupType,
} from 'types/types'

export { formatAliyahForStudent as formattedAliyahForStudent } from '@/util/Aliyah'
export { formatAliyahForTeacher as formattedAliyahForTeacher } from '@/util/Aliyah'
export { formatAliyahForTeacherWithSection as formattedAliyahForTeacherWithSection } from '@/util/Aliyah'
export { formatAliyahSimple as formattedAliyahSimple } from '@/util/Aliyah'
export { formatRank as formattedRank } from '@/util/Aliyah'
export { rankName as formattedRankName } from '@/util/Aliyah'
export { formatBarMitzvah as formattedBarMitzvah } from '@/util/BarMitzvah'
export { formatHaftarahForStudent as formattedHaftarahForStudent } from '@/util/Haftarah'
export { formatHaftarahForTeacher as formattedHaftarahForTeacher } from '@/util/Haftarah'
export { formatHaftarahSimple as formattedHaftarahSimple } from '@/util/Haftarah'

import { VOICE_MODE_SINGING } from '@/constants/practiceConstants'
import { formatParashah } from '@/util/Parashah'

function formattedParashah(parashah: ParashahType): string {
  return parashah ? formatParashah(parashah) : ''
}

/** Extract "chapter:verse" from a sefariaId like "Genesis.1:1" → "1:1". */
function verseRef(sefariaId: string): string {
  const dotIndex = sefariaId.indexOf('.')
  return dotIndex >= 0 ? sefariaId.slice(dotIndex + 1) : sefariaId
}

/** Format a compact verse range from all focus points' sefariaIds (e.g. "1:1–1:5"). */
function formatVerseRange(focusPoints: PracticeRecordingFocusPointType[]): string {
  const refs = focusPoints
    .map((fp) => fp.sentence?.sefariaId)
    .filter((id): id is string => !!id)
    .map(verseRef)
  if (!refs.length) {
    return ''
  }
  const sorted = [...new Set(refs)].sort((a, b) => {
    const [ac, av] = a.split(':').map(Number)
    const [bc, bv] = b.split(':').map(Number)
    return ac - bc || av - bv
  })
  if (sorted.length === 1) {
    return sorted[0]
  }
  return `${sorted[0]}–${sorted[sorted.length - 1]}`
}

/** Human-readable description of the current practice selection (aliyah, haftarah, or sentence group). */
function formatPracticeDescription(
  aliyah: AliyahType | null,
  haftarah: HaftarahType | null,
  sentenceGroup: SentenceGroupType | null,
): string {
  if (aliyah) {
    return `${aliyah.parashah.transliteratedTitle}, Aliyah ${aliyah.rank}`
  }
  if (haftarah) {
    return `Haftarah: ${haftarah.parashah.transliteratedTitle}`
  }
  if (sentenceGroup) {
    return sentenceGroup.reading.description ?? ''
  }
  return ''
}

/** Human-readable description of what was practiced, derived from focus points. */
function practiceRecordingDescription(recording: PracticeRecordingType): string {
  const fps = (recording.focusPoints?.filter(Boolean) ?? []) as PracticeRecordingFocusPointType[]
  const fp = fps[0]
  if (!fp) {
    return ''
  }
  const verses = formatVerseRange(fps)
  const suffix = verses ? ` (${verses})` : ''
  if (fp.aliyah) {
    return `${fp.aliyah.parashah.transliteratedTitle}, Aliyah ${fp.aliyah.rank}${suffix}`
  }
  if (fp.haftarah) {
    return `Haftarah: ${fp.haftarah.parashah.transliteratedTitle}${suffix}`
  }
  if (fp.sentenceGroup) {
    return (fp.sentenceGroup.reading.description ?? '') + suffix
  }
  return ''
}

/** Human-readable description of practice settings (voice, pronunciation, melody, read order). */
function practiceRecordingSettings(recording: PracticeRecordingType): string {
  const parts: string[] = []
  if (recording.voice) {
    parts.push(recording.voice === VOICE_MODE_SINGING ? 'Singing' : 'Reading')
  }
  if (recording.pronunciation?.description) {
    parts.push(recording.pronunciation.description)
  }
  if (recording.torahMelody?.description) {
    parts.push(recording.torahMelody.description)
  }
  if (recording.haftarahMelody?.description) {
    parts.push(recording.haftarahMelody.description)
  }
  if (recording.sentenceGroupMelody?.description) {
    parts.push(recording.sentenceGroupMelody.description)
  }
  if (recording.readOrder) {
    // Keys must match backend read_order values (READ_ORDER_STUDENT_ONLY etc. in bmt/constants.py)
    // and the ReadOrder enum names in usePracticeFSM.ts.
    const labels: Record<string, string> = {
      STUDENT_ONLY: 'Student only',
      STUDENT_TEACHER: 'Student then teacher',
      TEACHER_ONLY: 'Teacher only',
      TEACHER_STUDENT: 'Teacher then student',
    }
    const label = labels[recording.readOrder]
    if (label) {
      parts.push(label)
    }
  }
  return parts.join(', ')
}

/** Format an ISO date string with date and local time for display in tables. */
function formattedDate(dateStr: string): string {
  return new Date(dateStr).toLocaleString(undefined, {
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    month: 'short',
    year: 'numeric',
  })
}

/** Hebrew irregular plural forms. */
const HEBREW_PLURALS: Record<string, string> = {
  Aliyah: 'Aliyot',
  Haftarah: 'Haftarot',
  Parashah: 'Parshiyot',
}

/** Return the singular or plural form of a Hebrew word based on count. */
function hebrewPlural(count: number, singular: string): string {
  if (count <= 1) {
    return singular
  }
  const plural = HEBREW_PLURALS[singular]
  if (!plural) {
    throw new Error(`Unknown Hebrew singular: ${singular}`)
  }
  return plural
}

/** Format a date as a human-readable relative time string (e.g. "5m ago", "3d ago"). */
function formatTimeAgo(dateStr: string | null | undefined): string {
  if (!dateStr) {
    return 'Never'
  }
  const diffMs = Date.now() - new Date(dateStr).getTime()
  const diffMins = Math.floor(diffMs / 60000)
  if (diffMins < 60) {
    return `${diffMins}m ago`
  }
  const diffHours = Math.floor(diffMins / 60)
  if (diffHours < 24) {
    return `${diffHours}h ago`
  }
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays === 1) {
    return 'Yesterday'
  }
  return `${diffDays}d ago`
}

export {
  formatPracticeDescription,
  formattedDate,
  formattedParashah,
  formatTimeAgo,
  hebrewPlural,
  practiceRecordingDescription,
  practiceRecordingSettings,
}
